/**
 * R4S OmniMens TypeScript Types
 * Mirrors the Django backend models
 */

// ============ Core Types ============

export interface RestEndpoint {
  id: string;  // UUID from Django
  name: string;
  description?: string;
  base_url: string;
  path: string;
  method: string;
  supports_odata: boolean;
  odata_entity_set: string;
  connection_id: string | null;  // UUID reference to Connection
  discovered_fields: Record<string, FieldSchema>;
  last_schema_update: string | null;
  default_page_size: number;
  refresh_interval_seconds: number;
  max_records: number;  // Maximum records to fetch (0 = unlimited)
  full_url: string;
  is_active: boolean;
  last_fetched_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface FieldSchema {
  type: string;
  nullable: boolean;
  sample_values: string[];
  field_name?: string;
}

export interface RestResponse {
  id: number;
  endpoint: number;
  endpoint_name: string;
  data: Record<string, unknown>;
  odata_context: string;
  odata_next_link: string;
  record_count: number;
  query_params: Record<string, string>;
  fetched_at: string;
  response_time_ms: number | null;
  is_complete: boolean;
  error_message: string;
}

export interface ResponseRecord {
  id: number;
  response: number;
  endpoint: number;
  data: Record<string, unknown>;
  primary_key_value: string;
  indexed_string_1: string;
  indexed_string_2: string;
  indexed_string_3: string;
  indexed_date_1: string | null;
  indexed_number_1: number | null;
  fetched_at: string;
}

export interface FieldMapping {
  id: number;
  endpoint: number;
  source_field: string;
  indexed_column: string;
  display_name: string;
  data_type: string;
  is_primary_key: boolean;
  is_filterable: boolean;
  is_sortable: boolean;
  default_visible: boolean;
  format_pattern: string;
  created_at: string;
}

export interface Connection {
  id: string;  // UUID from Django
  name: string;
  description: string;
  base_url: string;
  tenant: string;
  auth_type: 'bearer' | 'oauth2' | 'basic';
  // OAuth2 specific (readable, not secrets)
  token_url?: string;
  client_id?: string;
  oauth2_scope?: string;
  // Custom headers for ERP (X-Infor-LnCompany, X-Infor-LnIdentity, etc.)
  custom_headers?: Record<string, string>;
  // Status
  token_expires_at?: string | null;
  is_active: boolean;
  has_credentials: boolean;
  has_service_account?: boolean;  // Whether service account credentials are configured
  last_used_at: string | null;
  last_error: string;
  created_at: string;
  updated_at: string;
}

// ============ Dashboard Types ============

export interface Dashboard {
  id: string;  // UUID from Django
  name: string;
  description: string;
  layout: LayoutItem[];
  auto_refresh: boolean;
  refresh_interval_seconds: number;
  is_public: boolean;
  created_by: string;
  is_template: boolean;
  widgets: Widget[];
  widget_count: number;
  created_at: string;
  updated_at: string;
}

export interface LayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
  maxW?: number;
  maxH?: number;
  static?: boolean;
}

export interface Widget {
  id: string;  // UUID from Django
  dashboard: string;  // Dashboard UUID
  name: string;
  widget_type: WidgetType;
  endpoint_id: string | null;  // UUID reference to RestEndpoint
  query_config: QueryConfig;
  config: WidgetConfig;
  grid_key: string;
  position_x: number;
  position_y: number;
  width: number;
  height: number;
  created_at: string;
  updated_at: string;
}

export type WidgetType =
  | 'table'
  | 'chart_line'
  | 'chart_bar'
  | 'chart_pie'
  | 'kpi'
  | 'status_grid'
  | 'status_indicator'
  | 'filter_bar'
  | 'sync_status'           // AI Planner Sync Status Widget
  | 'planning_scenarios'    // AI Planner Scenarios Overview Widget
  | 'capacity_heatmap'      // AI Planner Capacity Heatmap Widget
  | 'resource_timeline'     // AI Planner Resource Timeline Widget
  | 'conflict_list'         // AI Planner Conflict List Widget
  | 'scenario_comparison'   // AI Planner Scenario Comparison Widget
  | 'gantt_chart';          // AI Planner Gantt Chart Widget (SVAR)

export interface QueryConfig {
  $filter?: string;
  $select?: string;
  $orderby?: string;
  $expand?: string;
  $top?: number;
  $skip?: number;
}

export interface WidgetConfig {
  // Common
  title?: string;
  height?: number | string;  // Widget height (e.g., for Gantt chart)

  // Table
  columns?: TableColumn[];
  pageSize?: number;
  showPagination?: boolean;
  striped?: boolean;
  // NEW: Incremental Search / Column Filters (like ERP systems)
  showColumnFilters?: boolean;
  filterDebounceMs?: number;

  // Expandable Rows - for nested $expand data (e.g. OrderLines)
  expandable?: {
    enabled: boolean;
    dataField: string;          // Field with nested array (e.g. "OrderLines")
    columns: TableColumn[];     // Columns for the sub-table
    title?: string;             // Title for expanded section
    showCount?: boolean;        // Show count badge
  };

  // KPI
  aggregation?: 'count' | 'sum' | 'avg' | 'min' | 'max';
  field?: string;
  label?: string;
  format?: string;
  prefix?: string;
  suffix?: string;
  thresholds?: Threshold[];

  // Status Grid
  titleField?: string;
  subtitleField?: string;
  statusFields?: StatusField[];
  cardStyle?: 'compact' | 'detailed';
  columnsPerRow?: number;

  // Charts
  categoryField?: string;
  valueField?: string;
  xField?: string;
  yField?: string;
  seriesField?: string;
  orientation?: 'vertical' | 'horizontal';
  colors?: string[];
  smooth?: boolean;
  showDots?: boolean;
  donut?: boolean;
  showLabels?: boolean;
  showLegend?: boolean;

  // Status Indicator
  statusMapping?: string;
  showValue?: boolean;
  status?: 'success' | 'warning' | 'error' | 'info' | 'neutral';
  icon?: string;
  color?: string;
  subtitle?: string;

  // Filters (stored filter configuration)
  filters?: unknown;

  // OData Server Filters ($filter parameter)
  serverFilters?: Array<{
    field: string;
    operator: 'eq' | 'ne' | 'gt' | 'ge' | 'lt' | 'le' | 'contains' | 'startswith' | 'endswith';
    value: string | number | boolean | null;
    logic?: 'and' | 'or';
  }>;

  // OData $expand - Related Data (Relations)
  expandRelations?: ExpandRelation[];
}

// OData $expand Relation Configuration
export interface ExpandRelation {
  entity: string;           // Navigation Property Name (e.g. "OrderLines")
  targetEntity?: string;    // Target Entity Type (e.g. "SalesOrderLine")
  multiplicity?: '1' | '*'; // One-to-One or One-to-Many
  select?: string[];        // Optional: load only specific fields
  filter?: Array<{          // Optional: filter within the relation
    field: string;
    operator: string;
    value: string | number | boolean;
  }>;
  expand?: ExpandRelation[]; // Nested $expand
  enabled: boolean;         // Is this relation active?
}

export interface TableColumn {
  field: string;
  header: string;
  width?: number | string;
  sortable?: boolean;
  filterable?: boolean;
  format?: ColumnFormat;
  conditionalFormats?: ConditionalFormat[];
}

// Column formatting options (like Excel)
export interface ColumnFormat {
  type: 'text' | 'number' | 'currency' | 'percent' | 'date' | 'datetime' | 'boolean';
  decimals?: number;
  prefix?: string;
  suffix?: string;
  dateFormat?: string;
  thousandsSeparator?: boolean;
  nullValue?: string;
}

// Conditional formatting rule (like Excel)
export interface ConditionalFormat {
  id: string;
  condition: ConditionalFormatCondition;
  style: ConditionalFormatStyle;
}

export interface ConditionalFormatCondition {
  operator: 'equals' | 'not_equals' | 'greater' | 'greater_equal' | 'less' | 'less_equal' | 'between' | 'contains' | 'starts_with' | 'ends_with' | 'is_empty' | 'is_not_empty';
  value?: string | number;
  value2?: string | number; // For 'between' operator
}

export interface ConditionalFormatStyle {
  backgroundColor?: string;
  textColor?: string;
  fontWeight?: 'normal' | 'bold';
  icon?: string;
  iconColor?: string;
}

export interface Threshold {
  value: number;
  color: string;
}

export interface StatusField {
  field: string;
  label: string;
  statusMapping?: string;
}

// ============ Status Mapping Types ============

export interface StatusMapping {
  id: number;
  name: string;
  description: string;
  endpoint_id: number | null;
  source_field: string;
  rules: StatusRule[];
  default_color: string;
  default_icon: string;
  default_label: string;
  created_at?: string;
  updated_at?: string;
}

export interface StatusRule {
  /** Simple value match (frontend evaluator) */
  value?: unknown;
  /** Expression-based condition, e.g. "value == 'Active'" (backend evaluator) */
  condition?: string;
  color: string;
  icon: string;
  label: string;
}

export interface StatusResult {
  color: string;
  icon: string;
  label: string;
}

// ============ Email Template Types ============

export type EmailTemplateType =
  | 'scenario_success'
  | 'scenario_failure'
  | 'writeback_success'
  | 'writeback_failure'
  | 'custom';

export interface EmailTemplate {
  id: string;
  name: string;
  description: string;
  template_type: EmailTemplateType;
  subject_template: string;
  html_body: string;
  primary_color: string;
  is_default: boolean;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface EmailTemplatePreview {
  subject: string;
  html: string;
  text: string;
}

// ============ Widget Data Types ============

export interface WidgetData {
  // Generic widget data - shape depends on widget type
  value?: number | string;
  label?: string;
  trend?: number;
  trendLabel?: string;
  status?: 'success' | 'warning' | 'error' | 'info' | 'neutral';
  subtitle?: string;
  items?: StatusGridItem[];
  columns?: TableColumn[];
  rows?: Record<string, unknown>[];
  labels?: string[];
  datasets?: ChartDataset[];
  totalCount?: number;
  [key: string]: unknown;
}

export interface StatusGridItem {
  title: string;
  subtitle?: string;
  statuses: Array<{
    field: string;
    label: string;
    value: unknown;
    color?: string;
    icon?: string;
  }>;
  raw?: Record<string, unknown>;
}

export interface ChartDataset {
  label?: string;
  data: unknown[];
  backgroundColor?: string | string[];
  borderColor?: string | string[];
}

export interface WidgetDataResponse {
  widget_id: number;
  widget_type: WidgetType;
  data: WidgetData;
}

export interface TableData {
  columns: TableColumn[];
  rows: Record<string, unknown>[];
  totalCount?: number;
}

// ============ Column Filter Types (Incremental Search) ============

export type FilterOperator =
  | 'contains'      // [A] - Alphabetic/Text contains
  | 'starts_with'   // [A>] - Starts with
  | 'equals'        // [=] - Exact match
  | 'not_equals'    // [≠] - Not equals
  | 'greater'       // [>] - Greater than
  | 'greater_equal' // [≥] - Greater or equal
  | 'less'          // [<] - Less than
  | 'less_equal';   // [≤] - Less or equal

export interface ColumnFilter {
  value: string;
  operator: FilterOperator;
}

export interface ColumnFilters {
  [fieldName: string]: ColumnFilter;
}

// Operator labels for UI display (like in ERP systems)
export const FILTER_OPERATOR_LABELS: Record<FilterOperator, string> = {
  'contains': 'A',
  'starts_with': 'A>',
  'equals': '=',
  'not_equals': '≠',
  'greater': '>',
  'greater_equal': '≥',
  'less': '<',
  'less_equal': '≤',
};

// Default operators per column type
export const DEFAULT_OPERATORS_BY_TYPE: Record<string, FilterOperator> = {
  'text': 'contains',
  'number': 'greater_equal',
  'currency': 'greater_equal',
  'percent': 'greater_equal',
  'date': 'greater_equal',
  'datetime': 'greater_equal',
  'boolean': 'equals',
};

export interface KPIData {
  value: number;
  label: string;
  format: string;
  prefix: string;
  suffix: string;
  color: string | null;
}

export interface StatusGridData {
  cards: StatusCard[];
  totalCount: number;
}

export interface StatusCard {
  title: string;
  subtitle: string;
  statuses: CardStatus[];
  raw: Record<string, unknown>;
}

export interface CardStatus {
  field: string;
  label: string;
  value: unknown;
  mappingId?: string;
  color?: string;
  icon?: string;
}

export interface ChartData {
  labels?: string[];
  datasets: ChartDataset[];
}

export interface ChartConfig {
  colors?: string[];
  smooth?: boolean;
  showDots?: boolean;
  donut?: boolean;
  showLabels?: boolean;
  showLegend?: boolean;
}

// ============ API Response Types ============

export interface ApiResponse<T> {
  data: T;
  total_count?: number;
  page?: number;
  page_size?: number;
}

export interface ApiError {
  error: string;
  detail?: string;
}

// ============ Widget Type Info ============

export interface WidgetTypeInfo {
  type: WidgetType;
  name: string;
  description: string;
  icon: string;
  configSchema: Record<string, unknown>;
  defaultConfig: WidgetConfig;
}

// ============ Dashboard Template ============

export interface DashboardTemplate {
  id: number;
  name: string;
  description: string;
  category: string;
  template_config: {
    description: string;
    layout: LayoutItem[];
    auto_refresh: boolean;
    refresh_interval_seconds: number;
    widgets: Array<{
      name: string;
      type: WidgetType;
      grid_key: string;
      endpoint_id?: number;
      query_config?: QueryConfig;
      config: WidgetConfig;
    }>;
  };
  preview_image: string;
  required_endpoints: string[];
  created_at: string;
}

// ============ Machine Data Types (Sprint 7.1 — LN tirou461/462, tisfc400/401) ============

/** Machine capacity type classification */
export type MachineCapacityType = 'machine' | 'labor' | 'tool';

/** Machine operational status */
export type MachineStatus = 'available' | 'in_use' | 'maintenance' | 'offline';

/** Quantity reporting type */
export type MachineQuantityType = 'good' | 'reject' | 'scrap' | 'rework';

/**
 * MachineCapacityGroup — LN tirou461
 * Groups interchangeable machines within a work center
 */
export interface MachineCapacityGroup {
  id: string;
  erp_code: string;
  name: string;
  work_center: string;
  work_center_name: string;
  capacity_type: MachineCapacityType;
  default_efficiency: number;
  max_simultaneous: number;
  is_active: boolean;
  machine_count: number;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Compact list variant for MachineCapacityGroup */
export interface MachineCapacityGroupListItem {
  id: string;
  erp_code: string;
  name: string;
  work_center: string;
  capacity_type: MachineCapacityType;
  is_active: boolean;
}

/**
 * MachineNumber — LN tirou462
 * Individual machine with capacity, status, and cost information
 */
export interface MachineNumber {
  id: string;
  erp_code: string;
  name: string;
  capacity_group: string;
  capacity_group_name: string;
  work_center: string;
  work_center_name: string;
  status: MachineStatus;
  efficiency_rate: number;
  cost_per_hour: number;
  setup_time_factor: number;
  is_active: boolean;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Compact list variant for MachineNumber */
export interface MachineNumberListItem {
  id: string;
  erp_code: string;
  name: string;
  capacity_group: string;
  work_center: string;
  work_center_code: string;
  status: MachineStatus;
  is_active: boolean;
}

/**
 * MachineOperation — LN tisfc400
 * Machine assignment per operation for granular scheduling
 */
export interface MachineOperation {
  id: string;
  erp_order_number: string;
  erp_operation_sequence: number;
  operation: string | null;
  machine: string;
  machine_name: string;
  planned_setup_time: number;
  planned_run_time: number;
  planned_start: string | null;
  planned_end: string | null;
  actual_setup_time: number | null;
  actual_run_time: number | null;
  actual_start: string | null;
  actual_end: string | null;
  is_active: boolean;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Compact list variant for MachineOperation */
export interface MachineOperationListItem {
  id: string;
  erp_order_number: string;
  erp_operation_sequence: number;
  machine: string;
  planned_start: string | null;
  planned_end: string | null;
  is_active: boolean;
}

/**
 * MachineOperationQuantity — LN tisfc401
 * Reported quantities at machine level for progress tracking
 */
export interface MachineOperationQuantity {
  id: string;
  machine_operation: string;
  quantity_type: MachineQuantityType;
  quantity: number;
  reporting_date: string;
  reported_by: string | null;
  unit_of_measure: string | null;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Machine status summary response from /machines/status-summary/ */
export interface MachineStatusSummary {
  status: MachineStatus;
  count: number;
}

// ============ Calendar Types (Sprint 7.2 — LN tccom020/025/030, tirou010) ============

/** Calendar type classification */
export type CalendarType = 'standard' | 'shift' | 'flexible' | '24x7';

/** Calendar exception type */
export type CalendarExceptionType = 'holiday' | 'shutdown' | 'maintenance' | 'overtime' | 'special';

/** Day of week (0=Monday through 6=Sunday) */
export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/**
 * CalendarHeader — LN tccom020
 * Base calendar with code, validity period, and scheduling parameters
 */
export interface CalendarHeader {
  id: string;
  erp_code: string;
  name: string;
  calendar_type: CalendarType;
  effective_date: string | null;
  expiry_date: string | null;
  base_hours_per_day: number;
  base_days_per_week: number;
  timezone: string;
  time_windows: CalendarTimeWindow[];
  exceptions: CalendarException[];
  time_window_count: number;
  exception_count: number;
  is_active: boolean;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Compact list variant for CalendarHeader */
export interface CalendarHeaderListItem {
  id: string;
  erp_code: string;
  name: string;
  calendar_type: CalendarType;
  base_hours_per_day: number;
  base_days_per_week: number;
  time_window_count: number;
  exception_count: number;
  is_active: boolean;
}

/**
 * CalendarTimeWindow — LN tccom025
 * Available shifts/working times per day and calendar
 */
export interface CalendarTimeWindow {
  id: string;
  calendar: string;
  day_of_week: DayOfWeek | null;
  day_name: string;
  specific_date: string | null;
  start_time: string;
  end_time: string;
  efficiency: number;
  shift_name: string;
  duration_minutes: number;
  effective_minutes: number;
  is_active: boolean;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * CalendarException — LN tccom030
 * Holidays, plant shutdowns, maintenance windows, overtime
 */
export interface CalendarException {
  id: string;
  calendar: string;
  exception_type: CalendarExceptionType;
  name: string;
  description: string;
  start_date: string;
  end_date: string;
  override_start_time: string | null;
  override_end_time: string | null;
  capacity_factor: number;
  is_recurring: boolean;
  is_active: boolean;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * WorkCenterCalendarAssignment — LN tirou010
 * Assignment of calendars to work centers with priority
 */
export interface WorkCenterCalendarAssignment {
  id: string;
  work_center: string;
  work_center_code: string;
  calendar: string;
  calendar_code: string;
  calendar_name: string;
  priority: number;
  is_primary: boolean;
  effective_date: string | null;
  expiry_date: string | null;
  is_active: boolean;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Availability response from /calendar-headers/{id}/availability/ */
export interface CalendarAvailability {
  date: string;
  calendar_id: string;
  calendar_name: string;
  time_windows: Array<{
    start_time: string;
    end_time: string;
    efficiency: number;
    shift_name: string;
    duration_minutes: number;
    effective_minutes: number;
  }>;
  exceptions: Array<{
    name: string;
    exception_type: CalendarExceptionType;
    capacity_factor: number;
  }>;
  total_available_minutes: number;
  total_effective_minutes: number;
}

// Re-export filter types
export * from './filters';

// Re-export scheduling types (Phase 8)
export * from './scheduling';

// Re-export KPI, Site, Cost types (Phase 10)
export * from './kpi';

// Re-export Environment Variable types
export * from './environment';
